import useLocalStorage from "../../../hooks/useLocalStorage";
import { useState } from "react";
import { useLocation } from "react-router-dom";

/** Routes where the floating banner would overlap the full-screen app UI. */
const HIDDEN_PREFIXES = ["/dashboard", "/home"];

const policyPoints = [
  "You can report 100% anonymously — no name required",
  "Your data is encrypted and seen only by our review team",
  "We never sell or share your information with third parties",
  "You can ask us to delete your data at any time",
];

export default function CookieBanner() {
  const [dismissed, setDismissed] = useLocalStorage<boolean>("wits_whistleblower_v3", false);
  const [expanded, setExpanded]   = useState(false);
  const { pathname }              = useLocation();

  if (dismissed) return null;
  // Hide on the authenticated full-screen app where it would overlap fixed UI
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  return (
    <div
      style={{ zIndex: 9999, fontFamily: "var(--font-body)" }}
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[400px] max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-3rem)] animate-[slideUp_0.4s_ease-out]"
    >
      <div
        className="rounded-2xl overflow-hidden shadow-2xl shadow-black/50"
        style={{
          background: "linear-gradient(160deg, #063d1d 0%, #052e16 60%)",
          border: "1px solid rgba(74,222,128,0.30)",
        }}
      >
        {/* ── Bold accent bar ── */}
        <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #4ade80, #16a34a)" }} />

        {/* ── Header row ── */}
        <div className="flex items-start gap-3.5 p-5 pb-3.5">
          {/* Shield icon */}
          <div className="shrink-0 w-11 h-11 rounded-xl bg-green-500/20 border border-green-400/30 flex items-center justify-center">
            <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-green-400 text-[10px] font-bold tracking-[0.18em] uppercase mb-1">
              Whistleblower Protection
            </p>
            <p className="text-white text-base font-bold leading-tight" style={{ fontFamily: "var(--font-display)" }}>
              Your Identity Is Protected
            </p>
            <p className="text-white/65 text-xs mt-1.5 leading-relaxed">
              Report wildlife crime with confidence. Everything you share is
              <span className="text-white/90 font-semibold"> confidential, encrypted, and anonymous by default.</span>
            </p>
          </div>

          {/* Close */}
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="shrink-0 text-white/30 hover:text-white/70 transition-colors p-1 -mt-1 -mr-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Expanded policy ── */}
        {expanded && (
          <div className="px-5 pb-4 border-t border-white/8 pt-4">
            <ul className="space-y-2.5">
              {policyPoints.map((point) => (
                <li key={point} className="flex items-start gap-2.5 text-xs text-white/70 leading-snug">
                  <svg className="shrink-0 mt-0.5 w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-white/40 text-[10px] mt-4 leading-relaxed">
              Handled under international data-protection standards (GDPR) and Kenya&apos;s
              Data Protection Act, 2019. You have a legal right to privacy, access, and erasure.
            </p>
          </div>
        )}

        {/* ── Footer actions ── */}
        <div className="flex items-center gap-3 px-5 py-3.5 border-t border-white/10" style={{ background: "rgba(0,0,0,0.18)" }}>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-green-400 hover:text-green-300 transition-colors font-bold"
          >
            {expanded ? "Hide details ↑" : "How we protect you →"}
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="px-5 py-2 rounded-full bg-green-600 text-white text-xs font-bold hover:bg-green-500 transition-all shadow-lg shadow-green-900/40"
          >
            Got It
          </button>
        </div>
      </div>
    </div>
  );
}
